import { useLayoutContext } from 'contexts/LayoutContext';
import api from 'lib/trpcClient';
import AccountMappingGrid from './AccountMappingGrid';
import buildTransactionPairs from './buildTransactionPairs';
import { TransactionOperation } from 'further-types/lp/transaction';
import {
  ActionTransactionSubTypes,
  ActionTypes,
} from 'further-types/lp/action';
import { RealisationTransactionSubType } from 'further-types/lp/realisation';
import type { AccountMapping } from 'further-types/lp/account';
import { isHiddenAccountMappingRow } from './accountMappingRowVisibility';

const AccountMapping: React.FC = () => {
  const { selectedVehicleId } = useLayoutContext();
  const [vehicle] = api.vehicle.getById.useSuspenseQuery({
    vehicleId: selectedVehicleId ?? '',
  });

  const [actionTypes] = api.action.listTypes.useSuspenseQuery({
    vehicleId: selectedVehicleId ?? '',
  });

  const defaultAccountMappings = vehicle?.defaultAccountMappings ?? [];

  let formattedDefaultValues = buildTransactionPairs(defaultAccountMappings);

  // Filter out stale phantom mappings that were stored in the database but are no longer used at runtime
  formattedDefaultValues = formattedDefaultValues.filter((row) => {
    // Remove stale Realisation unwind sub-types (derived from CompanyRevaluationUp at runtime)
    if (
      row.transactionSubType ===
        RealisationTransactionSubType.UnrealisedGainsUnwind ||
      row.transactionSubType ===
        RealisationTransactionSubType.UnrealisedLossesUnwind
    ) {
      return false;
    }
    // Remove stale FX sub-types (derived at runtime)
    return true;
  });

  // Add any rows for actions that don't already have transaction type mappings
  actionTypes?.forEach((action) => {
    if (!action.subTypes.length) {
      // For actions with no sub-types, check if any mapping for this action exists
      if (
        !formattedDefaultValues.find(
          (mapping) =>
            mapping.actionType === action.actionType &&
            mapping.customActionType === action.customActionType,
        )
      ) {
        formattedDefaultValues.push({
          actionRef: action.action,
          actionType: action.actionType,
          customActionTypeId: action.customActionTypeId,
          actionCategory: action.actionCategory,
          customActionType: action.customActionType,
          transactionSubType: ActionTransactionSubTypes.CreditDebitPair,
          debitTransactionTypeId: '',
          creditTransactionTypeId: '',
          isInUse: false,
        });
      }
    } else {
      // For actions with sub-types, check each sub-type individually
      action.subTypes.forEach((subType) => {
        if (
          isHiddenAccountMappingRow({
            actionRef: action.action,
            actionType: action.actionType,
            transactionSubType: subType.type,
            debitTransactionTypeId: null,
            creditTransactionTypeId: null,
          })
        ) {
          return;
        }

        const existingRow = formattedDefaultValues.find(
          (mapping) =>
            mapping.actionType === action.actionType &&
            mapping.customActionType === action.customActionType &&
            mapping.transactionSubType === subType.type,
        );

        if (!existingRow) {
          // Special case: UnrealisedGainsUnwind uses CompanyRevaluationUp accounts (both disabled)
          let debitId: string | null = null;
          let creditId: string | null = null;

          if (
            subType.type === RealisationTransactionSubType.UnrealisedGainsUnwind
          ) {
            // Get accounts from CompanyRevaluationUp mapping
            const revalUpMapping = formattedDefaultValues.find(
              (m) => m.actionType === ActionTypes.CompanyRevaluationUp,
            );
            if (revalUpMapping) {
              debitId = revalUpMapping.debitTransactionTypeId || null;
              creditId = revalUpMapping.creditTransactionTypeId || null;
            }
          }

          formattedDefaultValues.push({
            actionRef: action.action,
            actionType: action.actionType,
            customActionTypeId: action.customActionTypeId,
            actionCategory: action.actionCategory,
            customActionType: action.customActionType,
            transactionSubType: subType.type,
            debitTransactionTypeId:
              subType.type ===
              RealisationTransactionSubType.UnrealisedGainsUnwind
                ? debitId
                : subType.operationType === TransactionOperation.Debit
                ? ''
                : null,
            creditTransactionTypeId:
              subType.type ===
              RealisationTransactionSubType.UnrealisedGainsUnwind
                ? creditId
                : subType.operationType === TransactionOperation.Credit
                ? ''
                : null,
            isInUse: false,
          });
        }
      });
    }
  });

  return (
    <AccountMappingGrid
      vehicleId={selectedVehicleId ?? ''}
      actionTypes={actionTypes ?? []}
      defaultAccountMappings={formattedDefaultValues}
    />
  );
};

export default AccountMapping;
